import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { type FieldErrors, useForm, type UseFormRegisterReturn } from "react-hook-form";
import type { Person } from "@/generated/core/v1/person";
import { ageOn, fromIso, toIso } from "@/plan-logic/lib/dates";
import { groupDigits, money } from "@/plan-logic/lib/format";
import { annualSalary, extraCoverage } from "@/plan-logic/decisions/changes";
import {
  defaultEventDate,
  heldFor,
  insuranceDefaults,
  type InsurancePersonValues,
  insuranceRun,
  insuranceSchema,
  type InsuranceValues,
  toInsuranceInput,
} from "@/plan-logic/decisions/values";
import { usePlanCtx } from "../PlanPage";
import { DateInput, Field, RunButton, useEdits } from "./fields";
import type { ToolFormProps } from "./forms";

/**
 * Insurance (v40–v44): a three-step wizard. Step one asks, per person and per product, what
 * coverage they hold (a yes or no that opens the amount and its own premium) and whether they
 * smoke; step two says what each product is for; step three picks who, which event, when, and the
 * coverage to test, then runs the event through the engine. No coverage formula runs here: the
 * amount tested is the user's (by default what they hold now), and whether it's enough comes from
 * the engine's plan against today's (ScenarioResults).
 */

export type InsuranceFormValues = InsuranceValues;
type Row = InsurancePersonValues;

export { defaultEventDate, heldFor, insuranceRun, toInsuranceInput };

const PRODUCTS = [
  { key: "life", title: "Life Insurance", icon: "🛡️" },
  { key: "ci", title: "Critical Illness Insurance", icon: "🩺" },
] as const;

const STEPS = ["What You Have", "What They Are For", "Recommended Coverage"];

function YesNo({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <span className="yn" role="group" aria-label={label}>
      <button
        type="button"
        className={`ynb${value ? " sel" : ""}`}
        aria-pressed={value}
        onClick={() => onChange(true)}
      >
        Yes
      </button>
      <button
        type="button"
        className={`ynb${value ? "" : " sel"}`}
        aria-pressed={!value}
        onClick={() => onChange(false)}
      >
        No
      </button>
    </span>
  );
}

function Seg<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
}) {
  const id = `seg-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="dmf">
      <label id={id}>{label}</label>
      <div className="dmseg" role="group" aria-labelledby={id}>
        {options.map(([v, l]) => (
          <button
            key={v}
            type="button"
            className={v === value ? "on" : ""}
            aria-pressed={v === value}
            onClick={() => onChange(v)}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The saved inputs of a scenario from before the wizard, or of this form. */
function fromSaved(saved: Record<string, unknown> | undefined): Partial<InsuranceFormValues> {
  if (!saved) return {};
  const out: Partial<InsuranceFormValues> = {};
  const str = (k: string) => (typeof saved[k] === "string" ? (saved[k] as string) : undefined);
  const event = str("event");
  if (event === "death" || event === "critical_illness") out.event = event;
  if (str("personUuid")) out.personUuid = str("personUuid");
  if (str("date")) out.date = str("date");
  if (str("durationYears")) out.durationYears = str("durationYears");
  // Before the wizard, the tested amount was "individualAmount".
  const coverage = str("coverage") ?? str("individualAmount");
  if (coverage !== undefined) out.coverage = coverage;
  if (str("extraPremium") !== undefined) out.extraPremium = str("extraPremium");
  if (Array.isArray(saved.people)) out.people = saved.people as Row[];
  return out;
}

export function InsuranceToolForm({ onRun, busy, defaults, onEdit, ran, result }: ToolFormProps) {
  const { family, index, today } = usePlanCtx();
  const people = family.persons;
  const name = (p: Person) => index.person(p.uuid) || "You";
  const saved = fromSaved(defaults);
  const base = insuranceDefaults(family, today, saved.people);
  const { register, handleSubmit, watch, setValue, formState } = useForm<InsuranceFormValues>({
    resolver: zodResolver(insuranceSchema),
    defaultValues: { ...base, ...saved, people: base.people },
  });
  useEdits(watch, onEdit);
  // An opened what-if goes straight to its answer.
  const [step, setStep] = useState(defaults ? 3 : 1);
  const e = formState.errors;
  const w = watch();
  const set = <K extends keyof InsuranceFormValues>(k: K, v: InsuranceFormValues[K]) =>
    setValue(k, v as never, { shouldDirty: true });
  const setRow = (i: number, k: keyof Row, v: boolean) =>
    setValue(`people.${i}.${k}` as const, v as never, { shouldDirty: true });

  const insured = people.find((p) => p.uuid === w.personUuid);
  const death = w.event === "death";
  const held = heldFor(w);
  const extra = extraCoverage(toInsuranceInput(w));
  const eventDate = fromIso(w.date);
  const ageThen = insured?.dob && eventDate ? ageOn(insured.dob, eventDate) : undefined;
  const product = death ? "Life Insurance Coverage" : "Critical Illness Coverage";

  const onInvalid = (errs: FieldErrors<InsuranceFormValues>) => {
    if (errs.people) setStep(1);
  };

  return (
    <form
      className="act-body dz-form dz-wiz"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = insuranceRun(v, family, today);
        onRun(run.changes, run.inputs, run.comparison);
      }, onInvalid)}
    >
      <div className="wizbar" data-testid="insurance-steps">
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            className={`wizs${step === i + 1 ? " on" : ""}`}
            aria-current={step === i + 1 ? "step" : undefined}
            onClick={() => setStep(i + 1)}
          >
            <i>{i + 1}</i>
            {s}
          </button>
        ))}
      </div>

      {step === 1 && (
        <div className="wizpg" data-testid="insurance-step-1">
          <h3>What coverage do you already have?</h3>
          <p className="wizlede">
            Life and critical illness only. If the answer is no, say no &mdash; it is an answer, not
            a gap in the form.
          </p>
          <div className="inspeople">
            {people.map((p, i) => {
              const row = w.people?.[i];
              const salary = annualSalary(family, p.uuid);
              const re = e.people?.[i];
              return (
                <div className="insperson" key={p.uuid} data-testid="insurance-person">
                  <div className="insname">
                    <b>{name(p)}</b>
                    <span>
                      {salary ? `${money(salary)} a year before tax` : "No salary in your plan"}
                    </span>
                  </div>
                  <div className="insprods">
                    {PRODUCTS.map((pr) => {
                      const has = pr.key === "life" ? !!row?.lifeHas : !!row?.ciHas;
                      const amt = pr.key === "life" ? "lifeAmount" : "ciAmount";
                      const prem = pr.key === "life" ? "lifePremium" : "ciPremium";
                      const err = pr.key === "life" ? re?.lifeAmount : re?.ciAmount;
                      return (
                        <div className={`insprod${has ? " on" : ""}`} key={pr.key}>
                          <div className="insq">
                            <span className="ic" aria-hidden="true">
                              {pr.icon}
                            </span>
                            <b>{pr.title}</b>
                            <YesNo
                              label={`${name(p)} holds ${pr.title}`}
                              value={has}
                              onChange={(v) =>
                                setRow(i, pr.key === "life" ? "lifeHas" : "ciHas", v)
                              }
                            />
                          </div>
                          {has && (
                            <div className="insfields">
                              <OField
                                id={`in-${pr.key}-amt-${i}`}
                                label="Amount covered"
                                reg={register(`people.${i}.${amt}`)}
                                error={err?.message}
                              />
                              <OField
                                id={`in-${pr.key}-prem-${i}`}
                                label="What it costs"
                                suffix="a month"
                                reg={register(`people.${i}.${prem}`)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="insq insmoke">
                    <b>Smoker</b>
                    <YesNo
                      label={`${name(p)} smokes`}
                      value={!!row?.smoker}
                      onChange={(v) => setRow(i, "smoker", v)}
                    />
                  </div>
                  {row?.smoker && (
                    <p className="hint">
                      Smoker rates are materially higher. It does not change the coverage you need,
                      only what an insurer will quote for it.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="wiznav">
            <span />
            <button type="button" className="btn p" onClick={() => setStep(2)}>
              See your recommended coverage
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="wizpg" data-testid="insurance-step-2">
          <h3>What these two things are actually for</h3>
          <div className="insedu">
            <div>
              <b>Life Insurance</b>
              <p>
                It replaces what a person contributes to the household, whether that contribution
                arrives as <b>income or as time</b>. A parent at home earns nothing on paper, but
                replacing what they do &mdash; childcare, driving, running the house &mdash; costs
                real money.
              </p>
              <p>
                <b>The point is the plan, not the payout.</b> Everything you told Abi mattered
                &mdash; the degree, the house, the retirement &mdash; your family can still reach
                without you, if the coverage is right. That is what this is buying.
              </p>
            </div>
            <div>
              <b>Critical Illness Insurance</b>
              <p>
                Over <b>two in five</b> Canadians develop cancer at some point in their lives, and a
                diagnosis usually means months away from work rather than the end of a career. This
                pays a lump sum on diagnosis, for the income that stops and the costs that start.
              </p>
              <p className="inscite">
                Statistics Canada, lifetime probability of developing cancer, 2023
              </p>
            </div>
            <div>
              <b>Optional Bereavement Amount</b>
              <p>
                Optional, and separate from replacing the income. Six or twelve months of a
                year&rsquo;s income as money to <b>stop</b> with &mdash; time off work, counselling,
                help in the house, the things a family needs in the first year and cannot plan for.
                It is not money the plan spends.
              </p>
            </div>
          </div>
          <div className="wiznav">
            <button type="button" className="btn g" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="btn p" onClick={() => setStep(3)}>
              Next
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="wizpg wizsplit" data-testid="insurance-step-3">
          <div className="wizleft">
            <Seg
              label="Primary Insured"
              value={w.personUuid}
              options={people.map((p) => [p.uuid, name(p)])}
              onChange={(v) => {
                set("personUuid", v);
                // The event's default date follows the person, until it's been set by hand.
                if (!formState.dirtyFields.date) {
                  const p = people.find((x) => x.uuid === v);
                  setValue("date", toIso(defaultEventDate(p, today)));
                }
              }}
            />
            <Seg
              label="Critical Event"
              value={w.event}
              options={[
                ["death", "A death"],
                ["critical_illness", "A serious illness"],
              ]}
              onChange={(v) => set("event", v)}
            />
            <Field
              label="When It Happens"
              error={e.date}
              htmlFor="in-date"
              hint={
                ageThen !== undefined
                  ? `${death ? "Death" : "Diagnosis"} at ${ageThen}, for ${insured ? name(insured) : "them"}.`
                  : undefined
              }
            >
              <DateInput id="in-date" reg={register("date")} />
            </Field>
            {!death && (
              <Seg
                label="Off Work For"
                value={w.durationYears}
                options={[
                  ["1", "1 year"],
                  ["2", "2 years"],
                  ["3", "3 years"],
                ]}
                onChange={(v) => set("durationYears", v)}
              />
            )}
            <OField
              id="in-cov"
              label={`${product} to Test`}
              reg={register("coverage")}
              error={e.coverage?.message}
              placeholder={held ? groupDigits(held) : "0"}
              hint={
                held
                  ? `Leave it blank to test the ${money(held)} held now.`
                  : "Nothing held now. Leave it blank to see what happens with no coverage."
              }
            />
            {extra > 0 && (
              <OField
                id="in-prem"
                label="Premium for the Extra Coverage"
                suffix="a month"
                reg={register("extraPremium")}
                error={e.extraPremium?.message}
                hint={`For the ${money(extra)} more, from an insurer's quote. Abi does not price insurance; left blank, the plan pays nothing for it.`}
              />
            )}
            <RunButton
              busy={busy}
              ran={ran}
              note="Abi re-runs your whole plan with the event in it and this coverage paying out on the day."
            />
          </div>
          <div className="wizright">{result}</div>
        </div>
      )}
    </form>
  );
}

/** Onboarding's rounded money field (v42's `.ofield`) in the hub's `.dmf` field. */
function OField({
  id,
  label,
  reg,
  suffix,
  error,
  hint,
  placeholder = "0",
}: {
  id: string;
  label: string;
  reg: UseFormRegisterReturn;
  suffix?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <div className="dmf">
      <label htmlFor={id}>{label}</label>
      <div className="ofield">
        <span className="pfx">$</span>
        <input id={id} type="text" inputMode="decimal" placeholder={placeholder} {...reg} />
        {suffix && <span className="sfx">{suffix}</span>}
      </div>
      {error ? (
        <p className="dz-err" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="hint">{hint}</p>
      )}
    </div>
  );
}
